import { Button } from "primereact/button";
import { InputTextarea } from "primereact/inputtextarea";
import React, { useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";

import {
  IDraftMessage,
  IDraftRunDetail,
} from "../../../store/queries/contractModelDrafts";
import { INITIAL_INSTRUCTIONS_CHIP, QUEUED_COMPOSER_HINT } from "../copy";
import { isProcessing } from "../utils";
import {
  buildThread,
  collapseLongText,
  collectOpenQuestions,
  formatThreadTime,
  isQueuedMessage,
  stepTitle,
} from "./conversation";
import { promptsToShow } from "./conversationPrompts";
import { StepId } from "./steps";

/** Side by side from here up; below it the conversation drops under the wizard and can be collapsed. */
const WIDE = "1200px";

/**
 * The page layout when a conversation is shown: the wizard on the left, the conversation docked on the
 * right. Exported so the page wraps its wizard in it rather than re-deriving the breakpoint.
 */
export const ChatLayout = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;

  @media (min-width: ${WIDE}) {
    flex-direction: row;
    align-items: flex-start;
  }
`;

export const ChatMain = styled.div`
  flex: 1 1 auto;
  min-width: 0;
`;

const Panel = styled.aside`
  display: flex;
  flex-direction: column;
  border: 1px solid var(--surface-border);
  border-radius: 6px;
  background: var(--surface-card);

  @media (min-width: ${WIDE}) {
    flex: 0 0 380px;
    width: 380px;
    position: sticky;
    top: 1rem;
    max-height: calc(100vh - 2rem);
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  border-bottom: 1px solid var(--surface-border);
`;

/** Collapsing only exists on narrow screens — docked on the right, the panel is always open. */
const CollapseToggle = styled(Button)`
  @media (min-width: ${WIDE}) {
    display: none !important;
  }
`;

const Body = styled.div<{ $collapsed: boolean }>`
  display: flex;
  flex-direction: column;
  min-height: 0;
  flex: 1 1 auto;

  @media (max-width: calc(${WIDE} - 1px)) {
    display: ${({ $collapsed }) => ($collapsed ? "none" : "flex")};
  }
`;

const Thread = styled.div`
  flex: 1 1 auto;
  overflow-y: auto;
  padding: 0.75rem 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  min-height: 8rem;
  max-height: 60vh;

  @media (min-width: ${WIDE}) {
    max-height: none;
  }
`;

const Bubble = styled.div<{ $mine: boolean; $muted?: boolean }>`
  align-self: ${({ $mine }) => ($mine ? "flex-end" : "flex-start")};
  max-width: 88%;
  padding: 0.5rem 0.75rem;
  border-radius: 12px;
  border-bottom-right-radius: ${({ $mine }) => ($mine ? "4px" : "12px")};
  border-bottom-left-radius: ${({ $mine }) => ($mine ? "12px" : "4px")};
  background: ${({ $mine }) =>
    $mine ? "var(--primary-50, #eef2ff)" : "var(--surface-100, #f3f4f6)"};
  min-width: 0;
  font-size: 0.9rem;
  line-height: 1.45;
  /* A reply can be a full rate list: keep its line breaks, wrap anything long, never scroll sideways. */
  white-space: pre-wrap;
  word-break: break-word;
  overflow-wrap: anywhere;
  overflow-x: hidden;
  opacity: ${({ $muted }) => ($muted ? 0.65 : 1)};
`;

const ShowAll = styled.button`
  display: block;
  margin-top: 0.35rem;
  padding: 0;
  border: 0;
  background: none;
  color: var(--primary-color);
  font: inherit;
  font-size: 0.8rem;
  cursor: pointer;
`;

/**
 * A message's text, collapsed to its first lines when it is long (a reply can carry every grouper / CPT
 * row). Its own component so each bubble remembers whether she expanded it.
 */
function MessageText({ body }: { body: string }) {
  const [expanded, setExpanded] = useState(false);
  const cut = useMemo(() => collapseLongText(body), [body]);
  if (!cut.collapsed) return <span>{body}</span>;
  return (
    <>
      {expanded ? body : `${cut.shown}\n…`}
      <ShowAll type="button" onClick={() => setExpanded((v) => !v)}>
        {expanded
          ? "Show less"
          : `Show all (${cut.hiddenLines} more line${
              cut.hiddenLines === 1 ? "" : "s"
            })`}
      </ShowAll>
    </>
  );
}

const Meta = styled.div<{ $mine: boolean }>`
  align-self: ${({ $mine }) => ($mine ? "flex-end" : "flex-start")};
  display: flex;
  align-items: center;
  gap: 0.4rem;
  margin-top: -0.35rem;
  font-size: 0.75rem;
  color: var(--text-color-secondary);
`;

const Chip = styled.span<{ $tone?: "warn" }>`
  padding: 0 0.4rem;
  border-radius: 999px;
  background: ${({ $tone }) =>
    $tone === "warn" ? "var(--orange-50, #fff7ed)" : "var(--surface-100)"};
  color: ${({ $tone }) =>
    $tone === "warn"
      ? "var(--orange-700, #c2410c)"
      : "var(--text-color-secondary)"};
  font-size: 0.72rem;
`;

const Separator = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.72rem;
  color: var(--text-color-secondary);

  &::before,
  &::after {
    content: "";
    flex: 1;
    border-top: 1px solid var(--surface-border);
  }
`;

const Composer = styled.div`
  padding: 0.75rem 1rem;
  border-top: 1px solid var(--surface-border);
`;

/** CLE-2811 — the suggested prompts: small, wrapping, never wider than the panel. */
const Suggestions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
  margin-bottom: 0.5rem;
`;

const Suggestion = styled.button`
  padding: 0.2rem 0.6rem;
  border: 1px solid var(--surface-border);
  border-radius: 999px;
  background: var(--surface-0, #ffffff);
  color: var(--text-color);
  font: inherit;
  font-size: 0.75rem;
  line-height: 1.35;
  text-align: left;
  cursor: pointer;

  &:hover,
  &:focus-visible {
    border-color: var(--primary-color);
    color: var(--primary-color);
  }
`;

interface IProps {
  run: IDraftRunDetail;
  /** The wizard step she is on — sent with her message as a hint to the agent about what she means. */
  activeStep: StepId;
  regenerating: boolean;
  onRegenerate: () => void;
  onDelete: (messageId: string) => void;
  /** Resolves with what the server did, or null when it failed (the page has already toasted). */
  onSend: (
    body: string,
    stepId: StepId
  ) => Promise<{ queuedOnly: boolean } | null>;
}

interface IOptimistic {
  body: string;
  stepId: StepId;
  /** The ids the run held when she sent — the bubble goes once a NEW message with her text arrives. */
  knownIds: Set<string>;
}

/**
 * CLE-2811 — the run as ONE conversation, like working with ChatGPT.
 *
 * She types and presses Enter; the agent re-derives the model and answers in words (the worker's
 * `reply`, or its change reasons). While a pass is running she can keep typing: those messages queue and
 * the server fires the next pass on its own when this one finishes, so nothing waits on her coming back.
 * The "Regenerate" button stays for the one case with nothing to say — "I uploaded a document, rerun".
 */
function ConversationPanel({
  run,
  activeStep,
  regenerating,
  onRegenerate,
  onDelete,
  onSend,
}: IProps) {
  const [draft, setDraft] = useState("");
  const [optimistic, setOptimistic] = useState<IOptimistic | null>(null);
  const [queuedHint, setQueuedHint] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const threadRef = useRef<HTMLDivElement>(null);
  const composerRef = useRef<HTMLTextAreaElement>(null);

  const working = regenerating || isProcessing(run.status);
  // CLE-2811 — the run instructions open the thread as her first bubble (they used to be a card above).
  const thread = useMemo(
    () =>
      buildThread(run.messages, run.versions ?? [], {
        runId: run.id,
        body: run.generationInstructions,
      }),
    [run.messages, run.versions, run.id, run.generationInstructions]
  );
  const hasConversation = thread.some((i) => i.kind !== "instructions");
  const suggestions = promptsToShow(run.stage, draft);
  const questions = useMemo(
    () => (working ? [] : collectOpenQuestions(run.recommendations)),
    [working, run.recommendations]
  );
  const anyQueued = run.messages.some(isQueuedMessage);
  const echoed =
    optimistic !== null &&
    run.messages.some(
      (m) => !optimistic.knownIds.has(m.id) && m.body === optimistic.body
    );
  const showOptimistic = optimistic !== null && !echoed;

  // Drop the optimistic bubble as soon as the server's copy of her message is in the run.
  useEffect(() => {
    if (echoed) setOptimistic(null);
  }, [echoed]);

  // The hint is about messages waiting on a pass; once nothing is waiting, it has nothing to say.
  useEffect(() => {
    if (!anyQueued) setQueuedHint(false);
  }, [anyQueued]);

  // Keep the newest thing in view, like any chat.
  useEffect(() => {
    const el = threadRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [thread.length, showOptimistic, working, questions.length]);

  const send = async () => {
    const body = draft.trim();
    if (!body) return;
    setOptimistic({
      body,
      stepId: activeStep,
      knownIds: new Set(run.messages.map((m) => m.id)),
    });
    setDraft("");
    const result = await onSend(body, activeStep);
    if (!result) {
      // Keep what she wrote — losing a paragraph to a network blip is the worst thing a chat can do.
      setOptimistic(null);
      setDraft(body);
      return;
    }
    setQueuedHint(result.queuedOnly);
  };

  // A suggestion is a starting point: it goes in the composer for her to edit or send, never sent for her.
  const pickSuggestion = (text: string) => {
    setDraft(text);
    window.setTimeout(() => {
      const el = composerRef.current;
      if (!el) return;
      el.focus();
      el.setSelectionRange(text.length, text.length);
    }, 0);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Enter sends, Shift+Enter is a newline. Never mid-IME composition (accented / CJK input).
    if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      send();
    }
  };

  const renderMessage = (m: IDraftMessage, queued: boolean) => {
    const mine = m.role !== "assistant";
    const where = mine ? stepTitle(m.stepId) : null;
    return (
      <React.Fragment key={m.id}>
        <Bubble $mine={mine}>
          <MessageText body={m.body} />
        </Bubble>
        <Meta $mine={mine}>
          {where ? <Chip>on {where}</Chip> : null}
          {m.recommendationId ? <Chip>about one item</Chip> : null}
          {queued ? <Chip $tone="warn">queued</Chip> : null}
          <span>{formatThreadTime(m.createdAt)}</span>
          {queued ? (
            // Only a message no pass has consumed can go — an applied one is part of how the model was
            // derived (the server refuses it with 409).
            <Button
              icon="pi pi-times"
              className="p-button-text p-button-rounded p-button-sm p-0"
              style={{ width: "1.25rem", height: "1.25rem" }}
              aria-label="Remove this queued message"
              tooltip="Remove"
              tooltipOptions={{ position: "top" }}
              onClick={() => onDelete(m.id)}
            />
          ) : null}
        </Meta>
      </React.Fragment>
    );
  };

  return (
    <Panel aria-label="Conversation with the AI">
      <Header>
        <i className="pi pi-comments text-color-secondary" />
        <span className="font-semibold">Conversation</span>
        <Button
          label="Regenerate"
          className="p-button-text p-button-sm ml-auto"
          tooltip="Re-read the documents and rebuild the model — e.g. after uploading a file"
          tooltipOptions={{ position: "left" }}
          disabled={working}
          onClick={onRegenerate}
        />
        <CollapseToggle
          icon={collapsed ? "pi pi-chevron-down" : "pi pi-chevron-up"}
          className="p-button-text p-button-sm p-button-rounded"
          aria-label={collapsed ? "Show conversation" : "Hide conversation"}
          onClick={() => setCollapsed((v) => !v)}
        />
      </Header>

      <Body $collapsed={collapsed}>
        <Thread ref={threadRef}>
          {!hasConversation && !showOptimistic && !working ? (
            <p className="m-0 text-sm text-color-secondary line-height-3">
              Tell the AI what to change &mdash; a value it misread, a page it
              missed, a network to leave out. It rebuilds the model and tells
              you what it did. Anything that doesn&rsquo;t change keeps your
              verdict.
            </p>
          ) : null}

          {thread.map((item) => {
            if (item.kind === "version") {
              return (
                <Separator key={item.key}>
                  Version {item.versionNo} · generated{" "}
                  {formatThreadTime(item.createdAt)}
                </Separator>
              );
            }
            if (item.kind === "instructions") {
              // Hers, but not a message row: no time, never queued, never deletable.
              return (
                <React.Fragment key={item.key}>
                  <Bubble $mine>
                    <MessageText body={item.body} />
                  </Bubble>
                  <Meta $mine>
                    <Chip>{INITIAL_INSTRUCTIONS_CHIP}</Chip>
                  </Meta>
                </React.Fragment>
              );
            }
            return renderMessage(item.message, item.queued);
          })}

          {showOptimistic && optimistic ? (
            <>
              <Bubble $mine $muted>
                {optimistic.body}
              </Bubble>
              <Meta $mine>
                {stepTitle(optimistic.stepId) ? (
                  <Chip>on {stepTitle(optimistic.stepId)}</Chip>
                ) : null}
                <span>sending…</span>
              </Meta>
            </>
          ) : null}

          {working ? (
            <Bubble $mine={false}>
              <i className="pi pi-spin pi-spinner mr-2" />
              Working on it&hellip;
            </Bubble>
          ) : null}

          {questions.length > 0 ? (
            <Bubble $mine={false}>
              <div className="font-semibold mb-1">Questions for you</div>
              <ul className="m-0 pl-3">
                {questions.map((q) => (
                  <li key={q} className="mb-1">
                    {q}
                  </li>
                ))}
              </ul>
            </Bubble>
          ) : null}
        </Thread>

        <Composer>
          {suggestions.length > 0 ? (
            <Suggestions aria-label="Suggested questions">
              {suggestions.map((text) => (
                <Suggestion
                  key={text}
                  type="button"
                  onClick={() => pickSuggestion(text)}
                >
                  {text}
                </Suggestion>
              ))}
            </Suggestions>
          ) : null}
          <div className="flex align-items-end gap-2">
            <InputTextarea
              ref={composerRef}
              className="w-full"
              rows={1}
              autoResize
              placeholder="Tell the AI what to change…"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={onKeyDown}
              style={{ maxHeight: "12rem", overflowY: "auto" }}
              aria-label="Message to the AI"
            />
            <Button
              icon="pi pi-send"
              aria-label="Send"
              disabled={draft.trim().length === 0}
              onClick={send}
            />
          </div>
          {queuedHint && anyQueued && working ? (
            <small className="block mt-1 text-color-secondary">
              {QUEUED_COMPOSER_HINT}
            </small>
          ) : (
            <small className="block mt-1 text-color-secondary">
              Enter to send &middot; Shift+Enter for a new line
            </small>
          )}
        </Composer>
      </Body>
    </Panel>
  );
}

export default ConversationPanel;
